import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import {
  useGetApiBookmarkFoldersQuery,
  usePutApiBookmarksByIdMutation,
} from '../../../redux/store/api/api';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addMessage } from '../../../redux/store/layout/slice';
import { selectContextBookmark } from '../../../redux/store/content/slice';

interface IRenameBookmarkDialogProps {
  dialogShow: boolean;
  setDialogShow: (show: boolean) => void;
  setLastUpdatedItem: (updatedId: string) => void;
}

function RenameBookmarkDialog({
  dialogShow,
  setDialogShow,
  setLastUpdatedItem,
}: IRenameBookmarkDialogProps) {
  const dispatch = useAppDispatch();
  const { t: translation } = useTranslation();
  const contextBookmark = useAppSelector(selectContextBookmark);
  const [updateBookmark, { isLoading, isError, error }] =
    usePutApiBookmarksByIdMutation();
  const [bookmarkName, setBookmarkName] = useState('');
  const [bookmarkNameIsValid, setBookmarkNameIsValid] = useState(true);
  const { refetch } = useGetApiBookmarkFoldersQuery();

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'UpdateBookmarkError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    setBookmarkName(contextBookmark.name || '');
    setBookmarkNameIsValid(true);
  }, [contextBookmark]);

  const handleRenameBookmark = () => {
    if (bookmarkName.trim().length === 0) {
      setBookmarkNameIsValid(false);
      return;
    }

    updateBookmark({
      id: contextBookmark.id || '',
      bookmark: { ...contextBookmark, name: bookmarkName.trim() },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateBookmarkSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        setDialogShow(false);
        refetch();
        setLastUpdatedItem(contextBookmark.id ?? '');
      });
  };

  return (
    <CustomDialog
      titleId='RenameBookmarkDialog'
      show={dialogShow}
      closeFunction={() => {
        setBookmarkName('');
        setBookmarkNameIsValid(true);
        setDialogShow(false);
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleRenameBookmark}
      actionTitle={translation('rename')}
      actionButtonDisabled={
        bookmarkName.trim() === contextBookmark.name || isLoading
      }
      dialogTitle={translation('renameBookmark')}>
      <>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <p>{translation('fieldsAreRequiredLegend')}</p>
            <Form.Group className='mb-3' controlId='BookmarkName'>
              <Form.Label>{translation('name')}*</Form.Label>
              <Form.Control
                value={bookmarkName}
                type='text'
                aria-describedby={
                  !bookmarkNameIsValid ? 'BookmarkNameInputError' : undefined
                }
                isInvalid={!bookmarkNameIsValid}
                onChange={(e) => {
                  setBookmarkName(e.target.value);
                  if (e.target.value.trim().length > 0) {
                    setBookmarkNameIsValid(true);
                  }
                }}
              />
              <Form.Control.Feedback id='BookmarkNameInputError' type='invalid'>
                {translation('fieldNotEmpty')}
              </Form.Control.Feedback>
            </Form.Group>
          </>
        )}
      </>
    </CustomDialog>
  );
}

export default RenameBookmarkDialog;
